import AgencySaleDialogView from 'features/Agency/components/AgencySaleDialogView';
import thunderBrickIcon from 'assets/icons/TMT/thunder-brick.svg';
import thunderIcon from 'assets/icons/TMT/thunder.svg';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import RFIDCard from 'features/Charge/ChargeRFID/components/RFIDCard';
import ChargeTransactionCompactListView from 'features/Charge/ChargeTransaction/components/ChargeTransactionCompactListView';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import AppRoute from 'general/constants/AppRoute';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import DialogAddVehicleView from '../DialogAddVehicleView';
import AppConstants from 'general/constants/AppConstants';

ChargeVehicleDetailView.propTypes = {
  vehicleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initialVehicle: PropTypes.object,
};

function ChargeVehicleDetailView({ vehicleId, initialVehicle = null }) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const [vehicleDetail, setVehicleDetail] = useState(initialVehicle);
  const isPlugAndChargeEnabled = (vehicle) =>
    vehicle?.chargingMode === ChargeConstants.VehicleChargingMode.SMART || vehicle?.autoCharge === true;
  const mergeVehicleDetail = useCallback((data) => {
    if (!_.isObject(data)) {
      return;
    }

    setVehicleDetail((prev) => ({
      ...prev,
      ...data,
      vehicleIdentity: data?.vehicleIdentity ?? prev?.vehicleIdentity,
      evccId: data?.evccId ?? prev?.evccId,
      chargingMode: data?.chargingMode ?? prev?.chargingMode,
      autoCharge: data?.autoCharge ?? prev?.autoCharge,
    }));
  }, []);
  const getVehicleIdentityId = useCallback(
    (vehicle) =>
      vehicle?.vehicleIdentityId ??
      vehicle?.vehicleIdentity?.vehicleIdentityId ??
      initialVehicle?.vehicleIdentityId ??
      initialVehicle?.vehicleIdentity?.vehicleIdentityId,
    [initialVehicle]
  );
  const loadVehicleDetail = useCallback(async () => {
    if (!vehicleId) {
      return;
    }

    const response = await ChargeHelper.API.Vehicle.requestGetVehicleDetail(vehicleId);
    if (!_.isObject(response)) {
      return;
    }

    mergeVehicleDetail(response);

    const vehicleIdentityId = getVehicleIdentityId(response);
    if (!Utils.isValid(vehicleIdentityId)) {
      return;
    }

    const vehicleIdentity = await ChargeHelper.API.Identifier.requestGetVehicleIdentityDetail(
      vehicleIdentityId
    );
    if (_.isObject(vehicleIdentity)) {
      mergeVehicleDetail({
        vehicleIdentity,
        evccId: vehicleIdentity?.code,
      });
    }
  }, [getVehicleIdentityId, mergeVehicleDetail, vehicleId]);
  const vehicleIdentityCode = vehicleDetail?.vehicleIdentity?.code ?? vehicleDetail?.evccId ?? '';
  const vehicleIdentityStatus = vehicleDetail?.vehicleIdentity?.status;
  const vehicleIdentityStatusColor =
    vehicleIdentityStatus === 'ACTIVE'
      ? AppColors.primary
      : vehicleIdentityStatus === 'INACTIVE'
        ? AppColors.danger
        : null;
  const vehicleIdentityStatusHint =
    vehicleIdentityStatus === 'ACTIVE'
      ? 'Được phép sạc'
      : vehicleIdentityStatus === 'INACTIVE'
        ? 'Bị chặn sạc'
        : '';
  const registeredChargingMode = isPlugAndChargeEnabled(vehicleDetail)
    ? 'Sạc nhận diện xe (Plug & Charge)'
    : 'Sạc thông thường';
  useEffect(() => {
    loadVehicleDetail();
  }, [loadVehicleDetail]);
  const [rfidCard, setRfidCard] = useState(null);
  useEffect(() => {
    if (vehicleDetail?.cards?.length > 0) {
      setRfidCard(vehicleDetail?.cards[0]);
    } else {
      setRfidCard(null);
    }
  }, [vehicleDetail]);

  // MARK: ---- METHODS ----
  const handleRefresh = () => {
    loadVehicleDetail();
  };
  const handleEdit = (row) => {
    AppCustomDialogHelper.show(
      'Sửa xe',
      <DialogAddVehicleView
        item={row}
        onCancel={() => AppCustomDialogHelper.hide()}
        onDone={() => {
          AppCustomDialogHelper.hide();
          handleRefresh();
        }}
      />,
      'lg',
      false,
      true
    );
  };
  const handleDelete = (row) => {
    ChargeHelper.UI.Vehicle.showDialogConfirmDeleteVehicle(row, () => {
      router.navigate(-1);
    });
  };
  const handleTogglePlugAndCharge = (row) => {
    const isPlugAndChargeActive = isPlugAndChargeEnabled(row);
    AppCustomDialogHelper.show(
      '',
      <AgencySaleDialogView
        iconEl={
          <img
            src={
              isPlugAndChargeActive
                ? AppResource.images.dialogIcons.dialogError
                : AppResource.images.dialogIcons.dialogSuccess
            }
          />
        }
        title={isPlugAndChargeActive ? 'Tắt sạc nhận diện xe' : 'Bật sạc nhận diện xe'}
        contentEl={
          <>
            Bạn có chắc muốn {isPlugAndChargeActive ? 'tắt' : 'bật'} tính năng sạc nhận diện xe
            (Plug &amp; Charge) cho xe{' '}
            <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray600)}>
              [{row?.plate}]
            </span>{' '}
            không?
          </>
        }
        cancelButtonText="Bỏ qua"
        confirmButtonText={isPlugAndChargeActive ? 'Tắt' : 'Xác nhận'}
        confirmButtonRed={isPlugAndChargeActive}
        onCancel={() => {
          AppCustomDialogHelper.hide();
        }}
        onConfirm={async () => {
          AppCustomDialogHelper.hide();
          const result = await ChargeHelper.API.Vehicle.requestUpdateAutoChargeConfig(
            row?.vehicleId,
            !isPlugAndChargeActive
          );
          if (result === true) {
            ToastHelper.showSuccess(
              isPlugAndChargeActive
                ? 'Đã tắt sạc nhận diện xe (Plug & Charge)'
                : 'Đã bật sạc nhận diện xe (Plug & Charge)'
            );
            handleRefresh();
            return;
          }

          ToastHelper.showError(result || 'Có lỗi xảy ra');
        }}
      />,
      'md',
      true,
      true
    );
  };

  const buildMoreActions = (row) => {
    const actions = [];
    const isPlugAndChargeActive = isPlugAndChargeEnabled(row);
    if (isAdmin) {
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.vehicleUpdate.code)) {
        actions.push({
          text: 'Chỉnh sửa',
          iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            handleEdit(row);
          },
        });
        actions.push({
          text: isPlugAndChargeActive
            ? 'Tắt sạc nhận diện xe (Plug & Charge)'
            : 'Bật sạc nhận diện xe (Plug & Charge)',
          iconElement: (
            <img
              src={isPlugAndChargeActive ? thunderBrickIcon : thunderIcon}
              alt=""
              style={{ width: '1.1rem', height: '1.1rem' }}
            />
          ),
          click: () => {
            handleTogglePlugAndCharge(row);
          },
        });
      }
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.vehicleDelete.code)) {
        actions.push({
          text: 'Xóa',
          iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            handleDelete(row);
          },
        });
      }
    }
    return actions;
  };

  return (
    <div>
      {/* breadcrumb */}
      <div>
        {UIHelper.buildBreadcrumb([
          {
            title: 'Danh sách xe',
            onPress: () => {
              router.navigate(-1);
            },
          },
          { title: 'Chi tiết xe' },
        ])}
      </div>
      <div className="row m-0 p-0 px-2 mt-4">
        {/* thong tin xe */}
        <div className="col-lg-8 mb-4 px-2">
          {UIHelper.buildCard(
            <div>
              {/* header */}
              <div className="d-flex align-items-center justify-content-between px-4 py-3">
                <div className="flex-fill">
                  <div className="d-flex align-items-center gap-3">
                    {/* avatar */}
                    <div className="">
                      {UIHelper.buildAvatar(
                        vehicleDetail?.model?.image
                          ? Utils.getFullUrl(vehicleDetail?.model?.image)
                          : null,
                        44,
                        AppResource.images.chargeConnectorTypes.vehicleDefault
                      )}
                    </div>
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center gap-2">
                        <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                          {vehicleDetail?.model?.name?.toUpperCase()}
                        </span>
                        <KTLabel
                          text={ChargeHelper.Utils.getVehicleStatusName(vehicleDetail?.status)}
                          isInline={true}
                          size={KTLabelSize.medium}
                          additionalClassName={`label-light-${ChargeHelper.Utils.getVehicleStatusColor(
                            vehicleDetail?.status
                          )}`}
                        />
                      </div>
                      <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
                        Biển số:{' '}
                        <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                          {vehicleDetail?.plate}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {UIHelper.buildMoreActionsButton(buildMoreActions(vehicleDetail))}
                </div>
              </div>

              <div className="row m-0 p-0 px-2">
                <div className="col-lg-6 mb-4 px-2">
                  <div>
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem(
                          'Loại xe',
                          _.find(
                            ChargeHelper.Data.vehicleTypeOptions,
                            (item) => item.value === vehicleDetail?.vehicleType
                          )?.name
                        )}
                        {UIHelper.buildCardCellItem(
                          'Hãng xe',
                          vehicleDetail?.model?.brand?.name,
                          () => {
                            Utils.openInNewTab(
                              `${AppRoute.manageVehicleBrand}/${vehicleDetail?.model?.brandId}`
                            );
                          }
                        )}
                        {UIHelper.buildCardCellItem('Dòng xe', vehicleDetail?.model?.name, () => {
                          Utils.openInNewTab(
                            `${AppRoute.manageVehicleBrand}/${vehicleDetail?.model?.brandId}`
                          );
                        })}
                        {UIHelper.buildCardCellItem(
                          'Khách hàng',
                          vehicleDetail?.user?.fullname,
                          () => {
                            Utils.openInNewTab(`${AppRoute.customerList}/${vehicleDetail?.userId}`);
                          }
                        )}
                        {UIHelper.buildCardCellItem(
                          'Số điện thoại',
                          vehicleDetail?.user?.phone,
                          null,
                          false
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 mb-4 px-2">
                  <div>
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem('Biển số xe', vehicleDetail?.plate)}
                        {UIHelper.buildCardCellItem('Số khung', vehicleDetail?.vinNumber)}
                        {UIHelper.buildCardCellItem(
                          'Dung tích pin',
                          Utils.formatNumber(vehicleDetail?.model?.batteryCapacity) + ' kWh'
                        )}
                        {UIHelper.buildCardCellItem(
                          'Đầu socket',
                          vehicleDetail?.model?.connectorType?.length ? ChargeHelper?.Utils?.getChargeConnectorTypeName(vehicleDetail?.model?.connectorType) : '',
                          null,
                          true
                        )}
                        {UIHelper.buildCardCellItem(
                          'Chế độ sạc đăng ký',
                          registeredChargingMode,
                          null,
                          true
                        )}
                        <div
                          className="d-flex align-items-center justify-content-between px-3 py-3"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[600])}>
                              Mã MAC/EVCC
                            </span>
                            {vehicleIdentityStatusColor && (
                              <KTTooltip offset={[0, 0]} text={vehicleIdentityStatusHint}>
                                <i
                                  className="fa-regular fa-circle-info cursor-pointer"
                                  style={{
                                    color: vehicleIdentityStatusColor,
                                    fontSize: '1rem',
                                  }}
                                />
                              </KTTooltip>
                            )}
                          </div>
                          <span
                            className="text-right"
                            style={{
                              maxWidth: '60%',
                              ...UIHelper.buildFontStyle('600', '13px', AppColors.gray[800]),
                            }}
                          >
                            {vehicleIdentityCode}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* thong tin the RFID */}
        <div className="col-lg-4 mb-4 px-2">
          <div>
            <RFIDCard row={rfidCard} vehicle={vehicleDetail} onRefresh={handleRefresh} />
          </div>
        </div>
      </div>

      <div className="px-4">
        {/* lich su sac */}
        <div>
          <ChargeTransactionCompactListView
            title="Lịch sử sạc"
            initialFilter={{
              vehicleId: vehicleId,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default ChargeVehicleDetailView;
